import React from 'react';
import styled from 'styled-components';
import { email } from '../utils/generalData.jsx';

// Assuming these components are in your project structure
import StyledButton from "../components/ui/StyledButton";
import {
    Section,
    SectionTitle,
    SubHeadline,
    Highlight,
    FeatureItem,
    FeatureTitle,
    FeaturesGrid,
    HeroSection,
    PageTitle,
    TechSpecsList,
    TechSpecItem,
    FigureWrapper,
    FigureCaption,
    USPSection,
    PageWrapper
} from '../components/ui/PageElements';
import {
    AudienceItem,
    AudienceList,
    FeatureList,
    FeatureListItem,
    RoadmapListItem,
    StyledList
} from "../components/ui/ListStyles.jsx";
import ResponsiveImage from '../components/ui/ResponsiveImage';
import PageMeta from '../components/PageMeta';

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    .fixed-button {
        width: 200px;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 10px;
        padding: 0 !important;
        box-sizing: border-box;
    }
`;

const SnobifyPage = () => {
    return (
        <PageWrapper>
            <PageMeta
                title="Snobify"
                description="Snobify - An anti-algorithm music discovery app for macOS. Explore 6,000+ genres, discover music by geography, and escape the popularity bubble. Coming soon."
                canonicalPath="/projects/snobify"
            />
            {/* --- Hero Section --- */}
            <HeroSection>
                <PageTitle>Snobify</PageTitle>
                <SubHeadline>Break Free from the Music Algorithm</SubHeadline>
                <SubHeadline>
                    Snobify was born from a simple question: <strong>Are there better ways to discover music?</strong> We created this app as a platform for exploring new, exciting, and more meaningful methods of musical discovery. It's a powerful <Highlight>anti-algorithm</Highlight> for your Mac, built on the belief that true discovery happens when you break the cycle of popularity bias.
                </SubHeadline>
                <FigureWrapper>
                    <ResponsiveImage
                        src="/assets/snobify/img/snobify-hero.png" // Suggested path for a hero animation
                        alt="Image showing the Snobify map view with country overlays and the menu bar widget."
                        sizes="(max-width: 600px) 90vw, 675px"
                    />
                    <FigureCaption>Your passport to a world of authentic musical exploration.</FigureCaption>
                </FigureWrapper>
                <StyledButton disabled>Download for macOS (Coming Soon)</StyledButton>
            </HeroSection>

            {/* --- Key Features Section --- */}
            <Section>
                <SectionTitle>New Ways to Discover</SectionTitle>
                <FeaturesGrid>
                    <FeatureItem>
                        <FeatureTitle>Explore 6,000+ Genres on Equal Footing</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem>Our menu bar <Highlight>Genre Explorer</Highlight> treats all genres—from global pop to Kurdish post-punk—with equal importance.</FeatureListItem>
                            <FeatureListItem>It's designed for <Highlight>guaranteed discovery</Highlight>, serving you styles you've never heard before.</FeatureListItem>
                            <FeatureListItem>You get a high-quality, <Highlight>representative track</Highlight> for an authentic first taste of each genre's best.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/snobify/img/popover.gif"
                                alt="Animated GIF of the Snobify menu bar popover cycling through different rare genres."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>Your personal music curator, living in your menu bar.</FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>

                    <FeatureItem>
                        <FeatureTitle>Travel the World Through Sound & Time</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem>Inspired by Radiooooo, our <Highlight>interactive map</Highlight> lets you explore music by place and time.</FeatureListItem>
                            <FeatureListItem>Pinpoint a country and a decade to unearth its <Highlight>unique sounds</Highlight>.</FeatureListItem>
                            <FeatureListItem>By design, our curated database <Highlight>avoids the overwhelming bias</Highlight> toward mainstream hits, giving you a more balanced global listening experience.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/snobify/img/snobifyMapAndTimeline.png"
                                alt="Snobify world map with glowing country overlays and timeline filtering."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>Pinpoint a location and a decade to explore its sounds.</FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>

                    <FeatureItem>
                        <FeatureTitle>Discover Music Anchored in History</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem>Our unique <Highlight>Event system</Highlight> anchors music in history and culture, beyond simple concert listings.</FeatureListItem>
                            <FeatureListItem>Explore pivotal human moments—from Bowie's final Ziggy Stardust show to Rostropovich playing cello at the <Highlight>fall of the Berlin Wall</Highlight>.</FeatureListItem>
                            <FeatureListItem>This is where data analysis meets passionate, <Highlight>human storytelling</Highlight>.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/snobify/img/event-sidebar.png" // Suggested path
                                alt="Screenshot of an event sidebar in Snobify, showing historical context for a song."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>Listen to the history as you explore it.</FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>

                </FeaturesGrid>
            </Section>
            
            {/* --- Target Audience --- */}
            <Section>
                <SectionTitle>Who is Snobify For?</SectionTitle>
                <AudienceList style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <AudienceItem><strong>The Musically Curious:</strong> Anyone tired of their recommendation bubble and hungry for genuinely new sounds.</AudienceItem>
                    <AudienceItem><strong>Music History Buffs:</strong> People who believe that the story behind the music is as important as the song itself.</AudienceItem>
                    <AudienceItem><strong>Geography & Travel Lovers:</strong> Explorers who want to connect with the world through its unique soundscapes.</AudienceItem>
                    <AudienceItem><strong>Data & Visualization Nerds:</strong> Anyone fascinated by the hidden connections between genres, artists, and places.</AudienceItem>
                </AudienceList>
            </Section>

            {/* --- Status & Future --- */}
            <Section>
                <SectionTitle>Always Exploring: The Future of Snobify</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    Our mission to find new ways of discovering music is just beginning. Snobify is a living project, and we're constantly experimenting with new ideas. Imagine...
                </SubHeadline>
                <StyledList style={{ maxWidth: '900px', margin: '0 auto 40px auto' }}>
                    <RoadmapListItem>Exploring music not just by name, but by its actual <Highlight>sound, mood, and texture</Highlight>.</RoadmapListItem>
                    <RoadmapListItem>Navigating a beautiful, <Highlight>interactive web of genres and artists</Highlight> to see how everything connects.</RoadmapListItem>
                    <RoadmapListItem>Tracing your favorite artist's career as they journeyed through <Highlight>different styles and scenes</Highlight>.</RoadmapListItem>
                </StyledList>
            </Section>
            
            {/* --- System Specs --- */}
            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>SYSTEM SPECIFICATIONS</SectionTitle>
                <TechSpecsList>
                    <TechSpecItem><span>Format</span><span>macOS App</span></TechSpecItem>
                    <TechSpecItem><span>Operating System</span><span>macOS (Apple Silicon)</span></TechSpecItem>
                    <TechSpecItem><span>Integration</span><span>Spotify Desktop App Required</span></TechSpecItem>
                </TechSpecsList>
            </Section>

            {/* --- Project Status --- */}
            <USPSection style={{ textAlign: 'center' }}>
                <SectionTitle>Project Status</SectionTitle>
                <SubHeadline style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
                    Snobify is fully functional, but I have decided to hold off on the release. The primary challenge is enabling Snobify to directly manage the Spotify playback queue, a feature currently constrained by the limitations of the Spotify API.
                </SubHeadline>
                <SubHeadline style={{ maxWidth: '800px', margin: '20px auto', textAlign: 'left' }}>
                    I am actively looking for a workaround or a hack to make this possible. If you know a solution, have suggestions, or simply want to try the software in its current state, please contact me.
                </SubHeadline>
                <ButtonWrapper>
                    <StyledButton
                        onClick={() => window.location.href = `mailto:${email}?subject=Snobify Inquiry`}
                        className="fixed-button"
                    >
                        Contact Me
                    </StyledButton>
                </ButtonWrapper>
            </USPSection>
        </PageWrapper>
    );
};

export default SnobifyPage;